import { useCallback, useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import type { AudioHTMLAttributes, Ref } from 'react'
import { learningAudioCache } from './learning-audio-cache'
import { observeLearningAudio } from './observe-learning-audio'

/** Keep native seek/rate/loop/events; prepare a single local source on visibility. */
export function CachedLearningAudio({ src, ref: forwardedRef, onPlay, onPause, onEnded, ...props }:
  AudioHTMLAttributes<HTMLAudioElement> & { readonly ref?: Ref<HTMLAudioElement> }) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [visible, setVisible] = useState(false)
  const [prepared, setPrepared] = useState<{ source: string; url: string; failed: boolean } | null>(null)
  const [playingSource, setPlayingSource] = useState<{ source: string; url: string } | null>(null)
  const source = typeof src === 'string' ? src : undefined
  const warmed = prepared?.source === source ? prepared : null
  const locked = playingSource && playingSource.source === source ? playingSource.url : undefined
  const status = !visible ? 'idle' : warmed ? warmed.failed ? 'fallback' : 'ready' : 'loading'
  const resolved = visible ? locked ?? warmed?.url : undefined
  const handleRef = useCallback((node: HTMLAudioElement | null) => {
    audioRef.current = node
    if (typeof forwardedRef === 'function') forwardedRef(node)
    else if (forwardedRef) forwardedRef.current = node
  }, [forwardedRef])

  useEffect(() => {
    const target = audioRef.current?.parentElement
    if (!target) return
    return observeLearningAudio(target, () => setVisible(true))
  }, [])

  useEffect(() => {
    const audio = audioRef.current
    const target = audio?.parentElement
    if (!audio || !target || !source) return
    // A genuine early activation keeps the native play() call in its gesture.
    // Normal preparation never assigns a remote src, avoiding parallel media
    // downloads from each native element before the shared fetch completes.
    const handleEarlyActivation = (event: Event) => {
      if (audio.getAttribute('src')) return
      if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') return
      flushSync(() => {
        setVisible(true)
        setPlayingSource({ source, url: source })
      })
    }
    target.addEventListener('click', handleEarlyActivation, true)
    target.addEventListener('keydown', handleEarlyActivation, true)
    return () => {
      target.removeEventListener('click', handleEarlyActivation, true)
      target.removeEventListener('keydown', handleEarlyActivation, true)
    }
  }, [source])

  useEffect(() => {
    if (!visible || !source) return
    let active = true
    const lease = learningAudioCache.acquire(source)
    lease.ready.then(url => {
      if (!active) return
      const audio = audioRef.current
      if (audio && !audio.paused) setPlayingSource({ source, url: audio.getAttribute('src') ?? source })
      else if (audio?.currentTime === 0) setPlayingSource(null)
      setPrepared({ source, url, failed: false })
    }, () => {
      // CORS/size/quota failure must preserve the original native media path.
      if (active) setPrepared({ source, url: source, failed: true })
    })
    return () => { active = false; lease.release() }
  }, [source, visible])

  return <audio {...props} autoPlay={visible && warmed ? props.autoPlay : false} data-audio-preload={status} onEnded={event => {
    setPlayingSource(null)
    onEnded?.(event)
  }} onPause={event => {
    // Natural completion dispatches pause before ended. Keep its source until
    // ended is delivered, otherwise a URL swap can suppress continuation/loops.
    if (event.currentTarget.paused && !event.currentTarget.ended && event.currentTarget.currentTime === 0) setPlayingSource(null)
    onPause?.(event)
  }} onPlay={event => {
    if (source) setPlayingSource({ source, url: event.currentTarget.getAttribute('src') ?? source })
    onPlay?.(event)
  }} preload={warmed ? 'auto' : 'none'} ref={handleRef} src={resolved} />
}
